import * as THREE from 'three';
import { jsondan } from '../../FikoThreeType';
import { FikoSahne } from '../FikoSahne';
import { FikoVektor3D } from '../FikoVektor3d';
import { FikoTakim } from './FikoTakim';
import { FikoTakimFrezeleme } from './FikoTakimFrezeleme';

export class FikoVektorTakimYol3D extends FikoVektor3D {
    type: any = "FikoVektorTakimYol3D";
    /** */
    takimYonu?: FikoVektor3D = new FikoVektor3D(0, 0, 1);
    hiz?: number;
    devir?: number;
    renk?: THREE.Color;

    takim?: FikoTakim & FikoTakimFrezeleme | any;

    ekBilgiler?: any;
    guncelSure?: number = null;
    toplamSure?: number = null;

    /**
     * G-code hareket tipi
     * - 'rapid': G00 hızlı hareket
     * - 'linear': G01 doğrusal kesim hareketi (feed)
     * - 'cut': G01 kesim hareketi (linear ile aynı)
     * - 'arc_cw': G02 saat yönünde yay
     * - 'arc_ccw': G03 saat yönü tersine yay
     * - 'dwell': G04 bekleme
     * - 'tap': G84 kılavuz çekme
     * - 'tap_retract': G84 kılavuz geri çekme
     */
    tip?: 'rapid' | 'linear' | 'cut' | 'feed' | 'arc_cw' | 'arc_ccw' | 'arc' | 'dwell' | 'tap' | 'tap_retract' = 'linear';

    /**
     * İlerleme hızı (mm/dk)
     */
    ilerleme?: number = 0;

    /**
     * Bekleme süresi (saniye) - G04 için
     */
    dwell?: number = 0;

    /**
     * Yay merkezi X ofseti (G02/G03 için)
     * I = merkez.x - başlangıç.x
     */
    i?: number = 0;

    /**
     * Yay merkezi Y ofseti (G02/G03 için)
     * J = merkez.y - başlangıç.y
     */
    j?: number = 0;

    /**
     * Yay merkezi Z ofseti (3D yaylar için, opsiyonel)
     * K = merkez.z - başlangıç.z
     */
    k?: number = 0;

    /**
     * Yay I değeri (alternatif isim)
     */
    arcI?: number = 0;

    /**
     * Yay J değeri (alternatif isim)
     */
    arcJ?: number = 0;

    /**
     * Yay yönü (true = saat yönünde CW)
     */
    arcCW?: boolean = true;

    /**
     * Kılavuz adımı (mm/devir) - G84 için
     */
    kilavuzAdim?: number = 0;

    constructor(x?: number, y?: number, z?: number, tip?: 'rapid' | 'linear' | 'cut' | 'feed' | 'arc_cw' | 'arc_ccw' | 'arc' | 'dwell' | 'tap' | 'tap_retract', ilerleme?: number, dwell?: number, kilavuzAdim?: number) {
        super(x, y, z);
        if (tip) this.tip = tip;
        if (ilerleme !== undefined) this.ilerleme = ilerleme;
        if (dwell !== undefined) this.dwell = dwell;
        if (kilavuzAdim !== undefined) this.kilavuzAdim = kilavuzAdim;
    }

    /**
     * Factory method for creating toolpath points with all parameters
     */
    static create(x: number, y: number, z: number, tip: 'rapid' | 'linear' | 'cut' | 'feed' | 'arc_cw' | 'arc_ccw' | 'arc' | 'dwell' | 'tap' | 'tap_retract' = 'linear', ilerleme?: number, dwell?: number, kilavuzAdim?: number): FikoVektorTakimYol3D {
        return new FikoVektorTakimYol3D(x, y, z, tip, ilerleme, dwell, kilavuzAdim);
    }

    /** Int encoding carpani (0.00001mm hassasiyet) */
    private static readonly INT_SCALE = 100000;
    /** Int'e cevir (x100000) */
    private static _i(v: number): number { return Math.round(v * FikoVektorTakimYol3D.INT_SCALE); }
    /** Float'tan int'e (takim boyutlari icin x100) */
    private static _i2(v: number): number { return Math.round(v * 100); }
    /** Renk hex string'e cevir */
    private static _hex(c: THREE.Color): string {
        return '#' + c.getHexString();
    }
    /** TakimYonu int array'e cevir (x100000) */
    private static _tyInt(ty: THREE.Vector3): number[] {
        const s = FikoVektorTakimYol3D.INT_SCALE;
        return [Math.round(ty.x * s), Math.round(ty.y * s), Math.round(ty.z * s)];
    }

    toJSON(meta?: { geometries: any; materials: any; textures: any; images: any }): any {
        const _i = FikoVektorTakimYol3D._i;
        const _i2 = FikoVektorTakimYol3D._i2;
        const ty = this.takimYonu;
        const _rslt: any = {
            type: this.type,
            // TakimYonu int array (x100000) - delta encoding FikoTakimYolu'nda yapilacak
            ty: ty ? FikoVektorTakimYol3D._tyInt(ty) : [0, 0, 100000],
            hiz: this.hiz,
            devir: this.devir,
            // Renk hex string (8 char vs 30+ char float array)
            r: this.renk ? FikoVektorTakimYol3D._hex(this.renk) : '#fff',
            takim: this.takim ? {
                type: this.takim.type,
                uuid: this.takim.uuid,
                cap: _i(this.takim.cap),
                radius: _i(this.takim.radius),
                boy: _i2(this.takim.boy),
                kesmeBoy: _i2(this.takim.kesmeBoy)
            } : null,
            ekBilgiler: this.ekBilgiler ? {
                ismi: this.ekBilgiler.ismi,
                programIsmi: this.ekBilgiler.programIsmi
            } : null,
            guncelSure: this.guncelSure != null ? _i2(this.guncelSure) : null,
            toplamSure: this.toplamSure != null ? _i2(this.toplamSure) : null,
            // Koordinatlar int (x100000) - 125.45678 -> 12545678
            x: _i(this.x),
            y: _i(this.y),
            z: _i(this.z),
            // G-code parametreleri
            tip: this.tip,
            i: this.i ? _i(this.i) : 0,
            j: this.j ? _i(this.j) : 0,
            k: this.k ? _i(this.k) : 0,
        }

        return _rslt;
    }

    static fromJson(sahne?: FikoSahne, parent?: any, json?: any) {
        const S = FikoVektorTakimYol3D.INT_SCALE;
        // Int mi float mi kontrol (eski format uyumlulugu)
        const isInt = Number.isInteger(json.x) && Math.abs(json.x) > 1000;
        const x = isInt ? json.x / S : json.x;
        const y = isInt ? json.y / S : json.y;
        const z = isInt ? json.z / S : json.z;

        const _rslt = new FikoVektorTakimYol3D(x, y, z);

        // TakimYonu: yeni format 'ty' veya eski format 'takimYonu'
        if (json.ty) {
            const tyInt = Number.isInteger(json.ty[0]) && Math.abs(json.ty[0]) > 100;
            _rslt.takimYonu.set(
                tyInt ? json.ty[0] / S : json.ty[0],
                tyInt ? json.ty[1] / S : json.ty[1],
                tyInt ? json.ty[2] / S : json.ty[2]
            );
        } else if (json.takimYonu) {
            _rslt.takimYonu.fromArray(json.takimYonu);
        }

        _rslt.hiz = json.hiz;
        _rslt.devir = json.devir;

        // Renk: yeni format 'r' (hex string) veya eski format 'renk' (float array)
        if (json.r) {
            _rslt.renk = new THREE.Color(json.r);
        } else if (json.renk) {
            _rslt.renk = new THREE.Color().fromArray(json.renk);
        }

        if (json.takim) {
            // Takim boyutlari int mi kontrol
            const capInt = Number.isInteger(json.takim.cap) && json.takim.cap > 100;
            const cap = capInt ? json.takim.cap / S : json.takim.cap;
            const radius = capInt ? json.takim.radius / S : json.takim.radius;
            const boy = capInt ? json.takim.boy / 100 : json.takim.boy;
            const kesmeBoy = capInt ? json.takim.kesmeBoy / 100 : json.takim.kesmeBoy;
            _rslt.takim = new FikoTakimFrezeleme(sahne, cap, radius, boy, kesmeBoy);
        }
        if (json.ekBilgiler) {
            _rslt.ekBilgiler = { ismi: json.ekBilgiler.ismi, programIsmi: json.ekBilgiler.programIsmi };
        }

        // Sure int mi (x100)
        const sureInt = json.guncelSure !== null && Number.isInteger(json.guncelSure) && json.guncelSure > 100;
        _rslt.guncelSure = sureInt ? json.guncelSure / 100 : json.guncelSure;
        _rslt.toplamSure = sureInt ? json.toplamSure / 100 : json.toplamSure;

        _rslt.tip = json.tip || 'linear';

        // Arc parametreleri int mi
        const arcInt = Number.isInteger(json.i) && Math.abs(json.i) > 100;
        _rslt.i = arcInt ? (json.i || 0) / S : (json.i || 0);
        _rslt.j = arcInt ? (json.j || 0) / S : (json.j || 0);
        _rslt.k = arcInt ? (json.k || 0) / S : (json.k || 0);

        return _rslt;
    }

}